import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import Pagination from '../../../../components/pagination/Pagination'
import FormLoader from '../../../../components/loader/FormLoader'
import { IntelligentCheckBadge } from '../kyc/IntelligentCheck'
import {
  ID_TYPE_LABELS,
  SHIPPER_REVIEW_META,
  ShipperIdentity,
  canViewShipperIds,
  initials,
} from './shared'
import '../kyc/KycReview.scss'
import './ShipperIds.scss'

type TabKey = 'all' | 'pending' | 'rejected' | 'approved' | 'flagged'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending review' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'approved', label: 'Approved' },
  { key: 'flagged', label: 'Flagged' },
]

const EMPTY_TAB_TEXT: { [key in TabKey]?: string } = {
  pending: 'No shipper IDs are waiting for review.',
  rejected: 'No shipper IDs are rejected.',
  approved: 'No shipper IDs have been approved yet.',
  flagged: 'No shipper IDs are flagged.',
}

type CheckCounts = { pass: number; flag: number; fail: number; unreviewed: number }

// Current image's intelligent check → ?intelligentCheck= (same as the KYC queue)
const CHECK_FILTERS: { key: '' | keyof CheckCounts; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'pass', label: 'Pass' },
  { key: 'flag', label: 'Flag' },
  { key: 'fail', label: 'Fail' },
  { key: 'unreviewed', label: 'Unreviewed' },
]

const ReviewPill = ({ identity }: { identity: ShipperIdentity }) => {
  const meta = SHIPPER_REVIEW_META[identity.status] || SHIPPER_REVIEW_META.pending
  return (
    <span className={`kyc-pill ${meta.className}`} title={identity.rejectionReason || ''}>
      {meta.label}
    </span>
  )
}

const ShipperIds = () => {
  const navigate = useNavigate()
  const { profile, notify } = useGlobalContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const businessId = searchParams.get('businessId') || ''

  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [identities, setIdentities] = useState<ShipperIdentity[]>([])
  const [pagination, setPagination] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [checkFilter, setCheckFilter] = useState<'' | keyof CheckCounts>('')
  const [checkCounts, setCheckCounts] = useState<CheckCounts | null>(null)

  const canView = canViewShipperIds(profile)

  const fetchIdentities = (page = 1, limit = 20) => {
    setLoading(true)
    let url = `admin/customer-identities?page=${page}&limit=${limit}`
    if (activeTab === 'flagged') url += '&flagged=true'
    else if (activeTab !== 'all') url += `&status=${activeTab}`
    if (businessId) url += `&businessId=${businessId}`
    if (search) url += `&search=${encodeURIComponent(search)}`
    if (checkFilter) url += `&intelligentCheck=${checkFilter}`
    get(url)
      .then((res: any) => {
        if (res.success) {
          setIdentities(res.data.identities)
          setPagination(res.data.pagination)
          setCheckCounts(res.data.intelligentCheckCounts || null)
        } else notify('error', res.message)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (!canView) return
    fetchIdentities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, search, businessId, checkFilter])

  if (!canView) {
    return (
      <div className='kyc-review shipper-ids'>
        <div className='restricted'>
          <Icon icon='mdi:shield-lock' />
          <h1>Restricted</h1>
          <p>Shipper IDs are only available to super admins and customer service.</p>
        </div>
      </div>
    )
  }

  const filteredBusinessName =
    businessId && identities[0]?.business?.businessId === businessId
      ? identities[0].business.org_name
      : null

  const openIdentity = (identity: ShipperIdentity) =>
    navigate(`/admin/shipper-id/${identity.customerIdentityId}`)

  return (
    <div className='kyc-review shipper-ids'>
      <div className='hero'>
        <div className='hero-text'>
          <h1>Shipper IDs</h1>
          <p>
            IDs that Business API customers register for the people they ship
            for. Bookings carry a copy of the ID they were made with.
          </p>
        </div>
      </div>

      <div className='header-tabs'>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className='search-row flex'>
        <div className='search flex'>
          <input
            type='text'
            placeholder='Search name, or a full ID number…'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput.trim())}
          />
          <button onClick={() => setSearch(searchInput.trim())}>
            <Icon icon='mdi:magnify' /> Search
          </button>
        </div>
        <div className='ic-filter flex'>
          <span className='ic-filter-label'>
            <Icon icon='mdi:shield-search' /> Intelligent check
          </span>
          {CHECK_FILTERS.map((f) => {
            const count = f.key && checkCounts ? checkCounts[f.key] : null
            return (
              <button
                key={f.key || 'all'}
                className={checkFilter === f.key ? 'active' : ''}
                onClick={() => setCheckFilter(f.key)}
              >
                {f.label}
                {count !== null && <span className='count'>{count}</span>}
              </button>
            )
          })}
        </div>
        {search && (
          <button
            className='clear-search'
            onClick={() => {
              setSearch('')
              setSearchInput('')
            }}
          >
            Clear <Icon icon='mdi:close' />
          </button>
        )}
        {businessId && (
          <button
            className='clear-search business-filter'
            onClick={() => setSearchParams({})}
            title='Show shipper IDs from every business'
          >
            <Icon icon='mdi:domain' />
            {filteredBusinessName || 'One business'}
            <Icon icon='mdi:close' />
          </button>
        )}
      </div>

      <div className='queue-card'>
        {loading ? (
          <FormLoader position='center' />
        ) : identities.length === 0 ? (
          <div className='empty-state'>
            <Icon icon='mdi:card-account-details-outline' />
            <h3>
              {search || activeTab !== 'all' || checkFilter
                ? 'Nothing found'
                : 'No shipper IDs yet'}
            </h3>
            <p>
              {search
                ? 'No shipper ID matches that name or number.'
                : checkFilter
                  ? 'No shipper IDs with that intelligent check result here.'
                  : EMPTY_TAB_TEXT[activeTab] ||
                    'They appear here once a business registers one through the API.'}
            </p>
          </div>
        ) : (
          <>
            <div className='table-wrap desktop-table'>
              <table>
                <thead>
                  <tr>
                    <th>Shipper</th>
                    <th>ID</th>
                    <th>Business</th>
                    <th>Status</th>
                    <th>Last updated</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {identities.map((identity) => (
                    <tr
                      key={identity.customerIdentityId}
                      onClick={() => openIdentity(identity)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className='wrap'>
                        <div className='customer-cell'>
                          <div className='avatar'>{initials(identity.fullName)}</div>
                          <div>
                            <p className='name'>
                              {identity.fullName}
                              {identity.flags.length > 0 && (
                                <span className='flag-icon' title='Flagged'>
                                  <Icon icon='mdi:flag' />
                                </span>
                              )}
                            </p>
                            {identity.externalCustomerId && (
                              <p className='email'>
                                Their ref: {identity.externalCustomerId}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <p className='id-type'>
                          {ID_TYPE_LABELS[identity.idType] || identity.idType}
                        </p>
                        <span className='mono'>{identity.idNumber}</span>
                      </td>
                      <td className='wrap'>{identity.business?.org_name || '—'}</td>
                      <td>
                        <div className='status-cell'>
                          <ReviewPill identity={identity} />
                          <IntelligentCheckBadge
                            review={identity.intelligentCheck}
                            showUnchecked
                          />
                        </div>
                      </td>
                      <td>
                        {new Date(identity.updatedAt).toLocaleDateString()}
                        <p className='version'>v{identity.currentVersion}</p>
                      </td>
                      <td className='icons' onClick={(e) => e.stopPropagation()}>
                        <button
                          className='review-btn'
                          onClick={() => openIdentity(identity)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='mobile-cards'>
              {identities.map((identity) => (
                <div
                  className='card'
                  key={identity.customerIdentityId}
                  onClick={() => openIdentity(identity)}
                >
                  <div className='row'>
                    <p className='name'>
                      {identity.fullName}
                      {identity.flags.length > 0 && (
                        <span className='flag-icon'>
                          <Icon icon='mdi:flag' />
                        </span>
                      )}
                    </p>
                    <ReviewPill identity={identity} />
                  </div>
                  <div className='row'>
                    <p>
                      <span className='mono'>{identity.idNumber}</span> ·{' '}
                      {ID_TYPE_LABELS[identity.idType] || identity.idType} ·{' '}
                      {identity.business?.org_name || '—'}
                    </p>
                  </div>
                  <div className='row'>
                    <p>
                      v{identity.currentVersion} · updated{' '}
                      {new Date(identity.updatedAt).toLocaleDateString()}
                    </p>
                    <IntelligentCheckBadge
                      review={identity.intelligentCheck}
                      compact
                      showUnchecked
                    />
                  </div>
                </div>
              ))}
            </div>

            {pagination && (
              <Pagination
                currentPage={pagination.page}
                totalCount={pagination.totalPages}
                pageSize={pagination.limit}
                onPageChange={(page: number, pageSize: number) =>
                  fetchIdentities(page, pageSize)
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ShipperIds
